import type { Edge, Node } from "@xyflow/react";

export type SequenceMessageMode = "ai" | "custom";

/** Domain tree node from Play `definitionJson` (not React Flow layout). */
export type SequenceDefinitionNode = {
  id: string;
  step?: string;
  type?: string;
  delay?: string;
  gate?: string;
  outcome?: string;
  next?: string;
  /** Play-level message copy mode for first / follow_up_2 / follow_up_3. */
  messageMode?: SequenceMessageMode;
  messageBody?: string;
  branches?: {
    on?: string;
    yes?: string;
    no?: string;
    positive?: string;
    hands?: string;
    none?: string;
  };
};

export const SEQUENCE_MESSAGE_STEPS = ["first", "follow_up_2", "follow_up_3"] as const;
export type SequenceMessageStep = (typeof SEQUENCE_MESSAGE_STEPS)[number];

export function isSequenceMessageStep(step?: string | null): step is SequenceMessageStep {
  return !!step && (SEQUENCE_MESSAGE_STEPS as readonly string[]).includes(step);
}

export type SequenceDefinition = {
  version?: number;
  entry?: string;
  nodes: SequenceDefinitionNode[];
};

export type GtmFlowNodeData = {
  label: string;
  subtitle?: string;
  stepKey?: string;
  count?: number;
  kind: "gtmStep" | "gtmBranch" | "gtmWait" | "gtmTerminal";
};

export type LinearStep = {
  id: string;
  label: string;
  subtitle?: string;
  kind: GtmFlowNodeData["kind"];
};

const COL_GAP = 220;
const ROW_GAP = 110;

function kindOf(n: SequenceDefinitionNode): GtmFlowNodeData["kind"] {
  if (n.type === "wait") return "gtmWait";
  if (n.type === "terminal") return "gtmTerminal";
  if (n.step === "branch" || n.branches) return "gtmBranch";
  return "gtmStep";
}

function labelOf(n: SequenceDefinitionNode): string {
  if (n.type === "wait") return `Wait ${formatDelay(n.delay)}`;
  if (n.type === "terminal") {
    return capitalize(n.outcome || n.step || n.id);
  }
  if (n.step === "branch" || n.branches?.on === "connected") return "Connected?";
  if (n.branches?.on === "replyClass") return "Reply?";
  return stepLabel(n.step || n.id);
}

function messageModeLabel(n: SequenceDefinitionNode): string {
  return n.messageMode === "custom" ? "Custom" : "AI";
}

function subtitleOf(n: SequenceDefinitionNode): string | undefined {
  if (n.type === "wait" && n.gate) return `After ${n.gate}`;
  if (n.step === "first") return `First message · ${messageModeLabel(n)}`;
  if (n.step === "follow_up_2") return `Follow-up 2 · ${messageModeLabel(n)}`;
  if (n.step === "follow_up_3") return `Follow-up 3 · ${messageModeLabel(n)}`;
  if (n.step === "connection") return "Connection request";
  return undefined;
}

function stepLabel(step: string): string {
  const map: Record<string, string> = {
    visit: "Visit profile",
    like: "Like post",
    connection: "Connect",
    first: "First message",
    follow_up_2: "Follow-up 2",
    follow_up_3: "Follow-up 3",
    ended: "Ended",
    meeting: "Meeting",
    hands: "Hands",
  };
  return map[step] || capitalize(step.replace(/_/g, " "));
}

function formatDelay(delay?: string): string {
  if (!delay) return "";
  if (delay === "PT6H") return "6h";
  if (delay === "P3D") return "3d";
  return delay;
}

function capitalize(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function parseDefinition(raw: string | SequenceDefinition): SequenceDefinition {
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw) as SequenceDefinition;
  } catch {
    return { nodes: [] };
  }
}

/** Pure mapper: domain `definitionJson` → React Flow nodes/edges. Positions are view-only. */
export function toReactFlowGraph(
  definitionJson: string | SequenceDefinition,
  enrollmentCounts?: Record<string, number>
): { nodes: Node<GtmFlowNodeData>[]; edges: Edge[]; linearSteps: LinearStep[] } {
  const def = parseDefinition(definitionJson);
  const byId = new Map(def.nodes.map((n) => [n.id, n]));
  const entry = def.entry && byId.has(def.entry) ? def.entry : def.nodes[0]?.id;
  if (!entry) return { nodes: [], edges: [], linearSteps: [] };

  const levels = new Map<string, number>();
  const order: string[] = [];
  const queue: string[] = [entry];
  levels.set(entry, 0);
  while (queue.length) {
    const id = queue.shift()!;
    order.push(id);
    const n = byId.get(id);
    if (!n) continue;
    const level = levels.get(id) ?? 0;
    const nextIds: string[] = [];
    if (n.next) nextIds.push(n.next);
    if (n.branches) {
      for (const key of ["yes", "no", "positive", "hands", "none"] as const) {
        const t = n.branches[key];
        if (t) nextIds.push(t);
      }
    }
    for (const nid of nextIds) {
      if (!byId.has(nid)) continue;
      if (!levels.has(nid)) {
        levels.set(nid, level + 1);
        queue.push(nid);
      }
    }
  }

  // Include orphan nodes not reached from entry
  for (const n of def.nodes) {
    if (!levels.has(n.id)) {
      levels.set(n.id, 0);
      order.push(n.id);
    }
  }

  const perLevel = new Map<number, string[]>();
  for (const id of order) {
    const lv = levels.get(id) ?? 0;
    const list = perLevel.get(lv) || [];
    list.push(id);
    perLevel.set(lv, list);
  }

  const nodes: Node<GtmFlowNodeData>[] = order.map((id) => {
    const n = byId.get(id)!;
    const kind = kindOf(n);
    const lv = levels.get(id) ?? 0;
    const row = (perLevel.get(lv) || []).indexOf(id);
    const stepKey = n.step || (n.type === "terminal" ? n.outcome : undefined) || n.id;
    return {
      id,
      type: kind,
      position: { x: lv * COL_GAP, y: row * ROW_GAP },
      data: {
        kind,
        label: labelOf(n),
        subtitle: subtitleOf(n),
        stepKey,
        count: enrollmentCounts?.[stepKey ?? ""],
      },
      draggable: false,
      connectable: false,
    };
  });

  const edges: Edge[] = [];
  const edgeSeen = new Set<string>();
  const pushEdge = (source: string, target: string, label?: string) => {
    const eid = `${source}->${target}:${label || ""}`;
    if (edgeSeen.has(eid) || !byId.has(target)) return;
    edgeSeen.add(eid);
    edges.push({
      id: eid,
      source,
      target,
      label,
      type: "smoothstep",
      animated: false,
      style: { stroke: "#94a3b8" },
      labelStyle: { fill: "#64748b", fontSize: 11, fontWeight: 500 },
    });
  };

  for (const n of def.nodes) {
    if (n.next) pushEdge(n.id, n.next);
    if (n.branches) {
      if (n.branches.yes) pushEdge(n.id, n.branches.yes, "Yes");
      if (n.branches.no) pushEdge(n.id, n.branches.no, "No");
      if (n.branches.positive) pushEdge(n.id, n.branches.positive, "Positive");
      if (n.branches.hands) pushEdge(n.id, n.branches.hands, "Hands");
      if (n.branches.none) pushEdge(n.id, n.branches.none, "No reply");
    }
  }

  const linearSteps: LinearStep[] = order.map((id) => {
    const n = byId.get(id)!;
    return {
      id,
      label: labelOf(n),
      subtitle: subtitleOf(n),
      kind: kindOf(n),
    };
  });

  return { nodes, edges, linearSteps };
}
